/**
 * Edits that combine several core ops, shared by the map tools and the side panel. Each is either a draft recipe
 * (for a drag's end or an apply) or a store action. Habitats that a trim or re-arrange removes go to the
 * Unplaced tray, never silently vanish.
 */
import { carveAreas, rearrangeHabitats } from '../../core/generate';
import { centroid, distPoly, pip, polyArea, type Derived } from '../../core/geom';
import { isAnimalArea, poolsOf } from '../../core/model';
import { addArea, deleteArea, deleteIcon, deleteRiver, deleteRoad, detachAreas, type OpResult } from '../../core/ops';
import { speciesNeeds } from '../../shared/species-needs';
import type { AreaId, PlanDoc, Pt, RoadId, Species, SpeciesId, UnplacedId } from '../../shared/types';
import { addToTray, type EditorStore, type Selection } from '../store/editor';
import { toast } from '../ui/shell/dialogs';

/** Species as an array for the core (empty until loaded). */
export function speciesList(store: EditorStore): Species[] {
  return store.getState().species ?? [];
}

/** Animal zones in the draft: id → species and count. */
function animalSnapshot(d: PlanDoc): Map<AreaId, { speciesId: SpeciesId; count: number }> {
  const m = new Map<AreaId, { speciesId: SpeciesId; count: number }>();
  for (const [id, a] of Object.entries(d.areas)) if (isAnimalArea(a)) m.set(id, { speciesId: a.speciesId, count: a.count });
  return m;
}

/** Result of trimming habitats off roads. */
export interface TrimResult {
  /** Zones that were trimmed (including removed ones). */
  trimmed: number;
  /** Zones that were squeezed out completely (now in doc.unplaced). */
  removed: number;
}

/**
 * Trims unlocked habitats off the given roads (carveAreas) inside a draft. Zones that end up with nothing left
 * go into the plan's Unplaced tray.
 */
export function trimAround(d: PlanDoc, roadIds: RoadId[], species: Species[]): TrimResult {
  const before = animalSnapshot(d);
  const { trimmed } = carveAreas(d, roadIds, species);
  let removed = 0;
  for (const [id, a] of before) if (!d.areas[id]) { addToTray(d, a.speciesId, a.count); removed++; }
  return { trimmed: trimmed.length, removed };
}

/** A friendly line about a trim, or '' when nothing happened. */
export function trimMessage(r: TrimResult | null, what = 'the path'): string {
  if (!r || !r.trimmed) return '';
  const kept = r.trimmed - r.removed;
  let msg = kept ? `Trimmed ${kept} habitat${kept === 1 ? '' : 's'} around ${what}.` : '';
  if (r.removed) msg += `${msg ? ' ' : ''}${r.removed} habitat${r.removed === 1 ? ' was' : 's were'} squeezed out and moved to Unplaced.`;
  return msg;
}

/** A round-ish outline (12 corners) around (x, y) with about `area` m² after smoothing. */
export function roundShape(x: number, y: number, area: number, size: number): Pt[] {
  // a 12-corner polygon with corner radius R has area 3R²; smoothing takes a few percent off
  const R = Math.sqrt((Math.max(area, 50) * 1.06) / 3);
  const cx = Math.min(size - R, Math.max(R, x)), cy = Math.min(size - R, Math.max(R, y));
  return Array.from({ length: 12 }, (_, i) => {
    const a = (i / 12) * Math.PI * 2;
    return [cx + Math.cos(a) * R, cy + Math.sin(a) * R] as Pt;
  });
}

/** Roads whose surface reaches into the outline `ring`. */
export function roadsCrossing(derived: Derived, ring: Pt[]): RoadId[] {
  return Object.entries(derived.roadPolys).filter(([, p]) => p.pts.some((q, i) =>
    pip(ring, q[0], q[1]) || distPoly(ring, q[0], q[1], true) < (p.widths[i] ?? 0) / 2)).map(([id]) => id);
}

/** Space a zone of `count` animals of `speciesId` needs (m²), with the plan's extra space. */
export function neededArea(store: EditorStore, speciesId: SpeciesId, count: number): number {
  const s = store.getState(), sp = s.species?.find((x) => x.id === speciesId);
  return sp ? speciesNeeds(sp, count, s.doc.settings.extraSpacePct).area : 400;
}

/**
 * Places a habitat from the tray (or a new one) at (x, y), sized to what it needs, then trims it off the paths
 * when "Habitats follow paths" is on. One undo step.
 */
export function placeHabitat(store: EditorStore, speciesId: SpeciesId, count: number, x: number, y: number, fromTray?: UnplacedId): void {
  const s = store.getState(), sp = s.species?.find((q) => q.id === speciesId);
  const pts = roundShape(x, y, neededArea(store, speciesId, count), s.doc.size);
  let newId: AreaId | null = null, trim: TrimResult | null = null;
  store.apply((d) => {
    const res = addArea(d, { points: pts, kind: sp?.kind ?? 'habitat', speciesId, count });
    if (!res.ok) return;
    newId = res.value;
    if (fromTray) delete d.unplaced[fromTray];
    const crossing = roadsCrossing(s.derived, pts);
    if (s.follow && crossing.length) trim = trimAround(d, crossing, speciesList(store));
  }, undefined, { label: 'Place habitat' });
  if (newId && store.getState().doc.areas[newId]) store.select({ kind: 'area', id: newId });
  store.say(trimMessage(trim, 'the paths'));
}

/** Below this share of its need, Make room says the new habitat came out too small (the analysis uses the same 97%). */
export const MAKE_ROOM_OK_SHARE = 0.97;

/** "1,250": whole square metres for messages. */
const m2 = (v: number) => Math.round(v).toLocaleString('en-GB');

/**
 * "Make room": re-arranges the unlocked habitats so a new one fits (a tiny seed shape is added near a habitat of
 * the same kind of land, then everything unlocked is re-sliced, the new one first so it gets the space it needs).
 * Zones that don't fit go to the tray. Says plainly when the new one got less than it needs.
 */
export function makeRoom(store: EditorStore, speciesId: SpeciesId, count: number, fromTray?: UnplacedId): void {
  const s = store.getState(), species = speciesList(store), sp = species.find((q) => q.id === speciesId);
  // start next to a habitat of the same biome, so it lands in that part of the walk
  let at: Pt = [s.doc.size / 2, s.doc.size / 2];
  for (const [id, a] of Object.entries(s.doc.areas)) {
    if (!isAnimalArea(a) || a.locked) continue;
    const other = species.find((q) => q.id === a.speciesId);
    const ring = s.derived.areaRings[id];
    if (other && sp && other.biome === sp.biome && ring && ring.length >= 3) { at = centroid(ring); break; }
  }
  const seedShape: Pt[] = [[at[0], at[1]], [at[0] + 2, at[1]], [at[0], at[1] + 2]];
  let newId: AreaId | null = null, out = 0, fitted = true;
  store.apply((d) => {
    const res = addArea(d, { points: seedShape, kind: sp?.kind ?? 'habitat', speciesId, count });
    if (!res.ok) return;
    newId = res.value;
    if (fromTray) delete d.unplaced[fromTray];
    out = rearrangeToTray(d, species, Math.floor(Math.random() * 1e9), newId);
    fitted = !!d.areas[newId];
  }, undefined, { label: 'Make room' });
  const after = store.getState(), ring = newId ? after.derived.areaRings[newId] : undefined;
  if (newId && after.doc.areas[newId]) store.select({ kind: 'area', id: newId });
  const others = out - (fitted ? 0 : 1), need = neededArea(store, speciesId, count), got = ring && ring.length >= 3 ? polyArea(ring) : 0;
  const pushed = others > 0 ? ` ${others} other ${others === 1 ? 'habitat' : 'habitats'} didn't fit and went to Unplaced.` : '';
  store.say(!fitted
    ? "There wasn't enough room, so it stays in Unplaced. Try making the zoo bigger or removing a habitat."
    : got < need * MAKE_ROOM_OK_SHARE
      ? `Made room, but there's only space for ${m2(got)} of the ${m2(need)} m² it needs, so it's too small. Make the zoo bigger, remove a habitat or unlock some, then try again.${pushed}`
      : `Made room by re-arranging the habitats.${pushed}`);
}

/**
 * Runs rearrangeHabitats in a draft (`priority` first, see there) and moves zones that didn't fit into the Unplaced
 * tray; returns how many.
 */
export function rearrangeToTray(d: PlanDoc, species: Species[], seed: number, priority?: AreaId): number {
  const { unplaced } = rearrangeHabitats(d, species, priority ? { seed, priority } : { seed });
  let n = 0;
  for (const id of unplaced) {
    const a = d.areas[id];
    if (!a || !isAnimalArea(a)) continue;
    addToTray(d, a.speciesId, a.count);
    deleteArea(d, { areaId: id });
    n++;
  }
  return n;
}

/** Re-arrange habitats (Advanced): re-slices every unlocked habitat around the roads and water. */
export function rearrangeAll(store: EditorStore): void {
  let out = 0;
  store.apply((d) => { out = rearrangeToTray(d, speciesList(store), Math.floor(Math.random() * 1e9)); }, undefined, { label: 'Re-arrange habitats' });
  store.say(out ? `Re-arranged the habitats. ${out} didn't fit and went to Unplaced.` : 'Re-arranged the habitats around the paths and water.');
}

/** Deletes the selected (or given) object. One undo step. */
export function deleteObject(store: EditorStore, sel: Selection): void {
  if (!sel || sel.kind === 'plot') return;
  const label = sel.kind === 'area' ? 'Delete area' : sel.kind === 'road' ? 'Delete path' : sel.kind === 'river' ? 'Delete river' : 'Delete icon';
  const res: OpResult = store.apply((d) => {
    if (sel.kind === 'area') return deleteArea(d, { areaId: sel.id });
    if (sel.kind === 'road') return deleteRoad(d, { roadId: sel.id });
    if (sel.kind === 'river') return deleteRiver(d, { riverId: sel.id });
    return deleteIcon(d, { iconId: sel.id });
  }, undefined, { label });
  if (!res.ok) toast(res.reason, 'error');
  else store.select(null);
}

/** Moves a habitat off the map into the tray. One undo step. */
export function moveToTray(store: EditorStore, areaId: AreaId): void {
  const s = store.getState(), a = s.doc.areas[areaId];
  if (!a || !isAnimalArea(a)) return;
  store.apply((d) => {
    const r = deleteArea(d, { areaId });
    if (r.ok) addToTray(d, a.speciesId, a.count);
    return r;
  }, undefined, { label: 'Move to Unplaced' });
  store.select(null);
}

/** Scales a zone around its centre to the space it needs (the prototype's "Resize to needed area"). */
export function resizeToNeed(store: EditorStore, areaId: AreaId): void {
  const s = store.getState(), st = s.analysis?.habitats[areaId], ring = s.derived.areaRings[areaId];
  if (!st || !ring || ring.length < 3 || !(st.need > 0)) return;
  const f = Math.sqrt(st.need / polyArea(ring));
  if (!Number.isFinite(f) || Math.abs(f - 1) < 0.005) return;
  store.apply((d) => {
    const set = [areaId, ...poolsOf(d, areaId)];
    detachAreas(d, set);
    const c = centroid(ring);
    const moved = new Set<string>();
    for (const id of set) for (const vid of d.areas[id]?.verts ?? []) {
      if (moved.has(vid)) continue;
      moved.add(vid);
      const v = d.verts[vid];
      if (v) { v.x = Math.min(d.size, Math.max(0, c[0] + (v.x - c[0]) * f)); v.y = Math.min(d.size, Math.max(0, c[1] + (v.y - c[1]) * f)); }
    }
  }, undefined, { label: 'Resize habitat' });
}
